import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const PDFViewer = ({
  isOpen,
  onClose,
  pdfUrl,
  title = "PDF Viewer",
  className = "",
  token = null
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (isOpen && pdfUrl) {
      setLoading(true);
      setError(null);
      setPageNumber(1);
      setScale(1);

      // Load PDF with authentication
      const loadPDF = async () => {
        try {
          const response = await fetch(pdfUrl, {
            credentials: 'include'
          });

          if (response.ok) {
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setBlobUrl(url);
            setLoading(false);
          } else {
            setError('Failed to load PDF');
            setLoading(false);
          }
        } catch (err) {
          setError('Failed to load PDF');
          setLoading(false);
        }
      };

      loadPDF();
    }

    // Cleanup blob URL when component unmounts or PDF changes
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isOpen, pdfUrl, token]);

  const handleLoad = () => {
    setLoading(false);
    setError(null);
  };

  const handleError = () => {
    setLoading(false);
    setError('Failed to load PDF. Please try again.');
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setScale(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setPageNumber(page);
    }
  };

  const previousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(prev => prev - 1);
    }
  };

  const nextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(prev => prev + 1);
    }
  };

  const downloadPDF = () => {
    if (blobUrl) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = title.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const printPDF = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.print();
    }
  };

  if (!isOpen) return null;

  const pdfContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`bg-white rounded-lg shadow-xl w-full max-w-[95vw] h-[95vh] flex flex-col ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{title}</h3>
            {totalPages > 0 && (
              <span className="text-sm text-gray-500">
                Page {pageNumber} of {totalPages}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* Zoom Controls */}
            <div className="flex items-center space-x-1 border border-gray-300 rounded-md">
              <button
                onClick={zoomOut}
                className="px-2 py-1 text-sm hover:bg-gray-100"
                title="Zoom Out"
              >
                −
              </button>
              <span className="px-2 py-1 text-sm text-gray-600 min-w-[3rem] text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={zoomIn}
                className="px-2 py-1 text-sm hover:bg-gray-100"
                title="Zoom In"
              >
                +
              </button>
              <button
                onClick={resetZoom}
                className="px-2 py-1 text-sm hover:bg-gray-100 border-l border-gray-300"
                title="Reset Zoom"
              >
                Reset
              </button>
            </div>

            {/* Page Navigation */}
            {totalPages > 1 && (
              <div className="flex items-center space-x-1 border border-gray-300 rounded-md">
                <button
                  onClick={previousPage}
                  disabled={pageNumber <= 1}
                  className="px-2 py-1 text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous Page"
                >
                  ←
                </button>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageNumber}
                  onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                  className="w-12 px-1 py-1 text-sm text-center border-0 focus:outline-none"
                />
                <button
                  onClick={nextPage}
                  disabled={pageNumber >= totalPages}
                  className="px-2 py-1 text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next Page"
                >
                  →
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <button
              onClick={printPDF}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              title="Print PDF"
            >
              Print
            </button>
            <button
              onClick={downloadPDF}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              title="Download PDF"
            >
              Download
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="flex-1 overflow-hidden min-h-0">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-600">Loading PDF...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-red-500 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && blobUrl && (
            <div className="h-full overflow-auto bg-gray-100 p-2">
              <div
                className="mx-auto bg-white shadow-lg"
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: 'top center',
                  width: 'fit-content',
                  minWidth: '800px'
                }}
              >
                <iframe
                  ref={iframeRef}
                  src={`${blobUrl}#page=${pageNumber}&zoom=${Math.round(scale * 100)}`}
                  className="w-full border-0"
                  onLoad={handleLoad}
                  onError={handleError}
                  title={title}
                  style={{
                    height: 'calc(100vh - 200px)',
                    minHeight: '600px',
                    width: '100%'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(pdfContent, document.body);
};

export default PDFViewer;
